import {
  Briefcase, DollarSign, Users, Building2, Truck, Landmark,
  TrendingUp, UserCircle, Lock, X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/design-system/lib/utils";
import type { MessageAttachmentDto } from "@/api/messaging";

/**
 * Internal Messaging v1.0 §7. Pill-shaped chip rendering an attached
 * Phoenix record. Used in two contexts:
 *   1. Composer — preview chips before send, with an X to remove (onRemove).
 *   2. Delivered message bubble — clickable, opens the record's detail page
 *      in a new tab. Falls back to gray + lock when the viewer can't access
 *      the record, or gray + strikethrough when the record was removed.
 *
 * Brand color per type comes from existing design-system tokens — no new
 * palette values introduced.
 */

export interface ResourceChipProps {
  attachment: MessageAttachmentDto;
  onRemove?: () => void;
}

const ICONS: Record<string, LucideIcon> = {
  deal: Briefcase,
  loan: DollarSign,
  broker: Users,
  buyer: Users,
  seller: Users,
  business: Building2,
  vendor: Truck,
  spv: Landmark,
  investor: TrendingUp,
  candidate: UserCircle,
};

// Per-type styling. Pulls from existing design-system tokens — `bg-onyx`,
// `bg-gold`, etc. No new palette values introduced.
const STYLES: Record<string, string> = {
  deal:      "bg-onyx text-white-pure",
  loan:      "bg-gold text-onyx",
  broker:    "bg-status-info/15 text-status-info border border-status-info/40",
  buyer:     "bg-white-warm text-onyx border border-border-light",
  seller:    "bg-white-warm text-onyx border border-border-light",
  business:  "bg-white-warm text-onyx border border-border-light",
  vendor:    "bg-status-success/15 text-status-success border border-status-success/40",
  spv:       "bg-onyx text-white-pure",
  investor:  "bg-status-error/15 text-status-error border border-status-error/40",
  candidate: "bg-gray-mid/15 text-onyx border border-gray-mid/40",
};

export function ResourceChip({ attachment, onRemove }: ResourceChipProps) {
  const Icon = ICONS[attachment.resourceType] ?? UserCircle;
  const style = STYLES[attachment.resourceType] ?? "bg-white-warm text-onyx border border-border-light";

  if (attachment.isDeleted) {
    // Gray + strikethrough; non-clickable.
    return (
      <span
        className={cn(
          "inline-flex items-center gap-space-1 px-space-2 py-[2px] rounded-pill text-label",
          "bg-border-light/60 text-gray-mid pointer-events-none line-through",
        )}
      >
        <Icon size={12} strokeWidth={1.5} aria-hidden />
        {attachment.displayLabel}
      </span>
    );
  }

  if (!attachment.viewerCanAccess && !onRemove) {
    // Gray + lock; non-clickable. Title gives the explanation on hover.
    return (
      <span
        title="You don't have permission to view this record."
        className={cn(
          "inline-flex items-center gap-space-1 px-space-2 py-[2px] rounded-pill text-label",
          "bg-border-light/60 text-gray-mid",
        )}
      >
        <Lock size={12} strokeWidth={1.5} aria-hidden />
        {attachment.displayLabel}
      </span>
    );
  }

  // Composer mode (onRemove set) — render as a static chip with an X.
  if (onRemove) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-space-1 px-space-2 py-[2px] rounded-pill text-label",
          style,
        )}
      >
        <Icon size={12} strokeWidth={1.5} aria-hidden />
        <span className="truncate max-w-[180px]">{attachment.displayLabel}</span>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${attachment.displayLabel}`}
          className="ml-[2px] -mr-[2px] rounded-full hover:bg-onyx/10 p-[1px]"
        >
          <X size={10} strokeWidth={1.5} aria-hidden />
        </button>
      </span>
    );
  }

  // Rendered chip in a delivered bubble — opens detail page in a new tab.
  return (
    <a
      href={attachment.detailUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex items-center gap-space-1 px-space-2 py-[2px] rounded-pill text-label",
        style,
        "no-underline hover:opacity-90 transition-opacity",
      )}
    >
      <Icon size={12} strokeWidth={1.5} aria-hidden />
      <span className="truncate max-w-[220px]">{attachment.displayLabel}</span>
    </a>
  );
}
